import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "@/components/ui-bits";

const PORTRAIT = "/foresight.jpg";

const CREDENTIALS = [
  "Licensed PI",
  "Real Estate & Fraud Investigation",
  "Corporate Due Diligence",
  "Security & Risk Consultancy",
];

export default function Founder() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section
      id="founder"
      ref={ref}
      className="relative bg-paper py-24 sm:py-32"
      data-testid="founder-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="relative overflow-hidden border border-navy/10">
                <motion.img
                  style={{ y: imgY, scale: 1.12 }}
                  src={PORTRAIT}
                  alt="Foresight Alhassan, Licensed Private Investigator"
                  className="w-full h-[480px] sm:h-[600px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-navy text-white px-6 py-4">
                <div className="font-display text-lg font-semibold">
                  Foresight Alhassan
                </div>
                <div className="text-gold text-xs uppercase tracking-[0.2em] mt-1">
                  Licensed Private Investigator
                </div>
              </div>
              <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-gold" />
            </div>
          </Reveal>

          {/* Bio */}
          <div className="lg:col-span-7">
            <Reveal>
              <Overline index={7}>Meet Foresight</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display font-bold text-navy leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl">
                Meet Foresight Alhassan
              </h2>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-ink/75 leading-relaxed text-[17px]">
                <p>
                  I'm based in Accra because I love Ghana — the energy, the
                  growth, the resilience. But throughout my career as a licensed
                  private investigator, I've seen too many people lose
                  everything because they loved Ghana so much they forgot to
                  verify.
                </p>
                <p>
                  That's why I founded BAT Ear Research. Not to scare you away —
                  to help you succeed, whether you're investing, relocating, or
                  need the truth about something closer to home.
                </p>
                <p>
                  I work for you. Whether my investigation says{" "}
                  <span className="text-navy font-semibold italic">'proceed'</span>{" "}
                  or <span className="text-navy font-semibold italic">'walk away,'</span> my fee stays the same. This
                  independence is your protection.
                </p>
                <p className="font-display text-xl text-navy italic">
                  Trust your gut. Then verify it.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-8 text-sm text-ink/60">
                — Foresight Alhassan, Licensed Private Investigator
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {CREDENTIALS.map((c) => (
                  <span
                    key={c}
                    className="text-xs uppercase tracking-wider font-medium text-navy border border-navy/20 px-3 py-2"
                    data-testid={`credential-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
